import {
  AddIcon,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@forgeglobal/design-framework';
import {
  Field,
  FieldRenderProps,
  Form,
} from '@tcs/common/components/atoms/Form';
import TextField from '@tcs/common/components/atoms/TextField';
import { useEffect, useState } from 'react';
import { object, string } from 'yup';

import { useStyles } from './style';

interface CreateQuestionFormProps {
  onSubmit: (question: string) => void;
  onVisible: () => void;
  questions: string[];
}

const CreateQuestionForm = ({
  onSubmit,
  onVisible,
  questions,
}: CreateQuestionFormProps) => {
  const classes = useStyles();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const onClickShowForm = () => {
    setIsFormVisible(true);
  };

  const onHideForm = () => {
    setIsFormVisible(false);
  };

  const handleOnSubmit = ({ question }: { question: string }) => {
    onSubmit(question);
    setIsFormVisible(false);
  };

  useEffect(() => {
    isFormVisible && onVisible();
  }, [isFormVisible, onVisible]);

  const validationSchema = object({
    question: string()
      .label('Question')
      .min(20, 'Please enter a longer question (between 20 and 80 characters).')
      .max(
        80,
        'Please enter a shorter question (between 20 and 80 characters).'
      )
      .matches(/^(?!\s*$).+/, { message: 'Question cannot be empty' })
      .notOneOf(
        questions,
        'Question cannot be the same as an existing question'
      )
      .required('Please enter your question'),
  });

  if (!isFormVisible) {
    return (
      <Box m={2} ml={0}>
        <Button
          className={classes.primaryButton}
          variant="text"
          size="small"
          disableRipple
          startIcon={<AddIcon />}
          onClick={onClickShowForm}
          data-test-id="create-your-own-button">
          Create your own
        </Button>
      </Box>
    );
  }

  return (
    <Form
      onSubmit={handleOnSubmit}
      validationSchema={validationSchema}
      render={({ handleSubmit, submitting }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Box p={2} className={classes.desktopForm}>
              <Grid container direction="column">
                <Grid item md={12}>
                  <Field
                    data-test-id="own-question-field"
                    label="Enter your question (20-80 characters required)"
                    name="question"
                    render={(props: FieldRenderProps<string>) => (
                      <TextField
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        {...props}
                      />
                    )}
                  />
                </Grid>
                <Grid container item justifyContent="flex-end">
                  <Button
                    size="medium"
                    color="primary"
                    type="submit"
                    variant="text"
                    disabled={submitting}
                    data-test-id="create-question-button">
                    Create
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Dialog
              open={isFormVisible && isMobile}
              transitionDuration={0}
              onBackdropClick={onHideForm}>
              <DialogTitle onClose={onHideForm}>Create your own</DialogTitle>
              <DialogContent className={classes.wrapper}>
                <div className={classes.content}>
                  <Typography variant="body2" className={classes.text}>
                    Enter your question (at least 25 characters)
                  </Typography>
                  <Field
                    data-test-id="own-question-field"
                    label="Enter your question (20-80 characters required)"
                    name="question"
                    render={(props: FieldRenderProps<string>) => (
                      <TextField
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        {...props}
                      />
                    )}
                  />
                  <Grid container spacing={1} className={classes.buttons}>
                    <Grid item>
                      <Button
                        color="secondary"
                        variant="text"
                        onClick={onHideForm}>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        size="medium"
                        color="primary"
                        type="submit"
                        variant="text"
                        disabled={submitting}
                        onClick={handleSubmit}
                        data-test-id="create-question-button">
                        Confirm
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </DialogContent>
            </Dialog>
          </form>
        );
      }}
    />
  );
};

export default CreateQuestionForm;
